/**
 * Puzzle — Master Mist Regulator.
 *
 * The canonical Incubation/Irrigation regulator described in
 * `docs/specs/SPEC-conservatory-rebuild.md` §2.2: a hero prop at the
 * bakery entrance with two opposing handles (Incubation + Irrigation).
 * Basil turns a handle with Turn-Table Touch; a finished crank flips the
 * whole room (`ConservatoryCycle`, owned by the scene shell and injected
 * here, the same pattern `TheOrreryPuzzle` uses for its lights).
 *
 * Two-cat rule (design principle #1): a petrified vine chokes the
 * regulator between the handles, and neither handle turns until it is
 * gone. Only Resonant Drop — Basil's ground-slam and Minna's purr
 * together (F+M, the cats within 5 u of each other) — shatters it, so the
 * first thing the cats do in Level 2 is use the co-op power Elder Kitty
 * gave them (principle #3). Then Basil flips the room twice: to
 * Incubation, then back to Irrigation. Minna's Purr can still pre-prime a
 * handle (three rhythm hits become two) — an assist, not a requirement.
 *
 * Solved after two flips requested by THESE handles. A flip from anywhere
 * else (the escort's mist valve, a QA `setPhaseImmediate`) doesn't count,
 * so no other crank can stand in for the choked regulator.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

import { ValveCrank } from "../../../entities/interactives/ValveCrank";
import { PetrifiedVine } from "../../../entities/interactives/PetrifiedVine";

import type { ConservatoryCycle } from "../../../systems/ConservatoryCycle";
import { GameEvents } from "../../../config/events";
import type { CueDef } from "../../../ui/tutorialCues";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

const REQUIRED_FLIPS = 2;

/** Regulator handles (hero prop at (22, 1.3, -2.1)). */
export const MASTER_VALVE_INCUBATION_POS = new Vector3(21.2, 1.3, -2.1);
export const MASTER_VALVE_IRRIGATION_POS = new Vector3(22.8, 1.3, -2.1);
/** The petrified vine coils around the feed pipe between the handles. */
export const MASTER_VALVE_VINE_POS = new Vector3(22, 1.75, -1.75);
export const MASTER_VALVE_VINE_KEY = "masterValveVine";
/**
 * A Resonant Drop shatters the vine when its epicentre (the cats'
 * midpoint) is within this many units of the vine along x, and within
 * VINE_SHATTER_REACH_Y vertically. Tighter than L1's blockage (15 u) so
 * the drop has to happen at the regulator, not from the spawn.
 */
export const VINE_SHATTER_REACH_X = 6;
const VINE_SHATTER_REACH_Y = 4;
/** Basil's Turn-Table Touch reach to a handle (world units). */
const CRANK_REACH = 4;
/** Minimum spacing between repeated "the vine holds" toasts (ms). */
const CHOKED_TOAST_COOLDOWN_MS = 2500;

// Cue chips (module constants: the driver re-renders only when the
// object changes).
const CUE_VINE: CueDef = { cat: "both", parts: [{ action: "resonantDrop" }, { text: "shatter the vine" }] };
const CUE_CRANK: CueDef = {
  cat: "basil",
  parts: [{ action: "turnStart" }, { text: "crank," }, { action: "rhythmHit" }, { text: "on the beat" }],
};
export const MASTER_VALVE_CUES = { vine: CUE_VINE, crank: CUE_CRANK } as const;

export type MasterValveStep = "vine" | "heat" | "cool" | "shifting" | "solved";

const STEP_OBJECTIVES: Record<Exclude<MasterValveStep, "solved">, string> = {
  vine: "Shatter the petrified vine",
  heat: "Heat the room: Incubation",
  cool: "Cool the room: Irrigation",
  shifting: "The room is shifting…",
};

export class MasterValvePuzzle implements PuzzleModule {
  readonly id = "masterValve";
  readonly displayName = "Master Mist Regulator";

  private ctx!: PuzzleContext;
  private conservatoryCycle: ConservatoryCycle;

  private incubationHandle!: ValveCrank;
  private irrigationHandle!: ValveCrank;
  private vine!: PetrifiedVine;

  private flipsCompleted = 0;
  private solvedEmitted = false;
  /** A flip one of our handles requested is under way (counts on settle). */
  private ownFlipPending = false;
  private transitionToastShown = false;
  private lastChokedToastAt = -Infinity;

  /** The handle currently being operated via TT Touch (if any). */
  private activeCrank: ValveCrank | null = null;

  constructor(conservatoryCycle: ConservatoryCycle) {
    this.conservatoryCycle = conservatoryCycle;
  }

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    this.incubationHandle = new ValveCrank(scene, {
      key: "masterValvePrimaryIncubation",
      position: MASTER_VALVE_INCUBATION_POS.clone(),
      variant: "incubation",
      label: "Incubation Handle",
    });

    this.irrigationHandle = new ValveCrank(scene, {
      key: "masterValvePrimaryIrrigation",
      position: MASTER_VALVE_IRRIGATION_POS.clone(),
      variant: "irrigation",
      label: "Irrigation Handle",
    });

    this.vine = new PetrifiedVine(scene, {
      key: MASTER_VALVE_VINE_KEY,
      position: MASTER_VALVE_VINE_POS.clone(),
      label: "Petrified Vine",
    });

    for (const crank of this.allCranks()) {
      ctx.registerInteractive({
        mesh: crank.rootMesh,
        highlightTarget: crank.getHighlightTarget(),
        hidden: false,
        state: "revealed",
      });
    }
    ctx.registerInteractive({
      mesh: this.vine.rootMesh,
      highlightTarget: this.vine.getHighlightTarget(),
      hidden: false,
      state: "revealed",
    });

    // Route TT Touch rhythm hits to the active handle.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
        if (this.activeCrank) {
          this.activeCrank.onInteract("turnTableTouch");
        }
      }),
    );

    // Minna's Purr pre-primes handles in range (3 rhythm hits → 2).
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PURR_PULSE, ({ sourcePosition, radius }) => {
        const src = new Vector3(sourcePosition.x, sourcePosition.y, sourcePosition.z);
        for (const crank of this.allCranks()) {
          if (crank.isPrimed || crank.isActivated) continue;
          if (Vector3.Distance(src, crank.rootMesh.position) <= radius) {
            crank.onInteract("calmingResonance");
          }
        }
      }),
    );

    // Resonant Drop at the regulator shatters the vine.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.RESONANT_DROP_TRIGGERED, (data) => {
        if (this.vine.isShattered) return;
        const vp = this.vine.rootMesh.position;
        if (Math.abs(data.x - vp.x) > VINE_SHATTER_REACH_X) return;
        if (Math.abs(data.y - vp.y) > VINE_SHATTER_REACH_Y) return;
        this.vine.shatter();
        ctx.gameEvents.emit(GameEvents.SHOCKWAVE_SHATTERED, {
          x: data.x,
          y: data.y,
          z: data.z,
          radius: data.radius,
          blockageKey: MASTER_VALVE_VINE_KEY,
        });
        ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "The petrified vine shatters — the regulator turns free!",
        });
        ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, { puzzleId: this.id, step: "vineShattered" });
      }),
    );

    // Only our handles' requests are ours to forward; the escort's mist
    // valve handles its own.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CONSERVATORY_CYCLE_REQUESTED, (data) => {
        const sourceCrank = this.findCrankByKey(data.sourceKey);
        if (!sourceCrank) return;
        if (this.conservatoryCycle.requestPhaseFlip(data.target)) {
          this.ownFlipPending = true;
          return;
        }
        // Hysteresis / same-phase / mid-transition rejection: reset the
        // source handle so the player can retry once the lockout clears
        // (otherwise it stays latched at `activated` and swallows hits).
        sourceCrank.resetCycle();
        ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: this.conservatoryCycle.canonicalPhase === data.target
            ? `The room is already in ${data.target === "incubation" ? "Incubation" : "Irrigation"} — turn the other handle.`
            : "The regulator hisses but holds — it's still stabilizing.",
        });
      }),
    );

    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CONSERVATORY_CYCLE_TRANSITION, () => {
        if (!this.ownFlipPending || this.transitionToastShown) return;
        this.transitionToastShown = true;
        ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Master regulator engaged — room is shifting.",
        });
      }),
    );

    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CONSERVATORY_PHASE_CHANGED, () => {
        for (const crank of this.allCranks()) {
          crank.resetCycle();
        }
        // The flip the player was cranking toward has happened — tear
        // down the rhythm ring now instead of leaving it spinning until
        // Basil wanders 4u away.
        if (this.activeCrank) {
          this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
          this.activeCrank = null;
        }
        if (!this.ownFlipPending) return;
        this.ownFlipPending = false;
        this.flipsCompleted += 1;
        // Sub-step progress carries puzzleId/step only; the level-scope
        // counter (1/3, 2/3, 3/3) is LevelCueDriver's.
        ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, { puzzleId: this.id, step: `flip${this.flipsCompleted}` });
        this.maybeSolve();
      }),
    );
  }

  update(_dt: number, _elapsed: number): void {
    // Proximity-based TT Touch deactivation: if Basil walks away from the
    // active handle, tear down the ability so the rhythm ring doesn't
    // linger. Same pattern as SeizedCrane's lever guard.
    if (this.activeCrank) {
      const dist = Vector3.Distance(this.ctx.basil.mesh.position, this.activeCrank.rootMesh.position);
      if (dist >= CRANK_REACH) {
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
        this.activeCrank = null;
      }
    }
  }

  onAbilityKey(key: string, pressed: boolean, _released: boolean): boolean {
    // 1 = toggle Turn-Table Touch on the nearest handle
    if (key === "1" && pressed) {
      if (this.activeCrank) {
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
        this.activeCrank = null;
        return true;
      }
      const nearest = this.nearestCrank(this.ctx.basil.mesh.position);
      if (!nearest) return false;
      if (!this.vine.isShattered) {
        // The vine seizes both handles: consume the key (no rhythm ring)
        // and say what frees them.
        this.toastChoked();
        return true;
      }
      // Route through the holder so ABILITY_ACTIVATED fires for
      // HUD/telemetry/banter; only latch the handle when activation
      // succeeded (a refusal emits ABILITY_BLOCKED and leaves no target).
      this.ctx.turnTableTouch.setTarget(nearest.rootMesh.position, () => {});
      if (this.ctx.basilAbilities.activateAbility("turnTableTouch")) {
        this.activeCrank = nearest;
      }
      return true;
    }

    // 2 = rhythm hit → routes to the active handle's registerHit()
    if (key === "2" && pressed && this.activeCrank) {
      this.ctx.turnTableTouch.attemptHit();
      return true;
    }

    return false;
  }

  getState(): PuzzleState {
    if (this.flipsCompleted >= REQUIRED_FLIPS) return { solved: true, progress: 1 };
    // Three steps: the vine, then each flip.
    const steps = (this.vine?.isShattered ? 1 : 0) + this.flipsCompleted;
    return { solved: false, progress: steps / (REQUIRED_FLIPS + 1) };
  }

  /** Current step of the regulator sequence (drives getCue). */
  getStep(): MasterValveStep {
    if (this.flipsCompleted >= REQUIRED_FLIPS) return "solved";
    if (!this.vine.isShattered) return "vine";
    if (this.conservatoryCycle.isTransitioning) return "shifting";
    return this.conservatoryCycle.isIrrigation ? "heat" : "cool";
  }

  getCue(): PuzzleCue | null {
    const step = this.getStep();
    if (step === "solved") return null;
    const objective = STEP_OBJECTIVES[step];
    switch (step) {
      case "vine":
        return { objective, cue: MASTER_VALVE_CUES.vine, anchor: above(this.vine.rootMesh.position, 1.2) };
      case "heat":
        return { objective, cue: MASTER_VALVE_CUES.crank, anchor: above(this.incubationHandle.rootMesh.position, 2.0) };
      case "cool":
        return { objective, cue: MASTER_VALVE_CUES.crank, anchor: above(this.irrigationHandle.rootMesh.position, 2.0) };
      default:
        return { objective, cue: null };
    }
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "crank_primary_0", position: { x: MASTER_VALVE_INCUBATION_POS.x, y: MASTER_VALVE_INCUBATION_POS.y }, radius: 1.2, allowedCat: "basil", label: "Incubation handle" },
        { id: "crank_primary_1", position: { x: MASTER_VALVE_IRRIGATION_POS.x, y: MASTER_VALVE_IRRIGATION_POS.y }, radius: 1.2, allowedCat: "basil", label: "Irrigation handle" },
        // Resonant Drop target: both cats stand here (within 5 u of each
        // other) and press F+M; the drop's midpoint must land within
        // VINE_SHATTER_REACH_X of the vine.
        { id: "petrified_vine", position: { x: MASTER_VALVE_VINE_POS.x, y: MASTER_VALVE_VINE_POS.y }, radius: 1.5, allowedCat: "either", label: "Petrified vine (Resonant Drop target)" },
      ],
      traversals: [
        // The two handles are 1.6 u apart, inside Basil's 4 u reach of
        // both, so the auto-release in update() never fires between
        // them: press "1" twice (toggle off, then on) to switch handles.
        {
          id: "tt_touch_switch_crank",
          kind: "ability",
          cat: "basil",
          from: "crank_primary_0",
          to: "crank_primary_1",
          successWindowMs: 1_000,
          recipe: [
            { key: "1", holdMs: 80 },
            { waitMs: 200 },
            { key: "1", holdMs: 80 },
          ],
        },
        // Co-op chord at the regulator: both keys held together so
        // AbilityInputMapper sees them pressed in the same frame.
        {
          id: "coop_resonant_drop_vine",
          kind: "coop",
          cat: "either",
          from: "petrified_vine",
          to: "petrified_vine",
          successWindowMs: 3_000,
          recipe: [
            { keyDown: "f" },
            { keyDown: "m" },
            { waitMs: 200 },
            { keyUp: "m" },
            { keyUp: "f" },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     vineShattered?: boolean,       // clear the vine (no shatter FX)
   *     flipsCompleted?: number,       // explicit flip count (clamps to REQUIRED_FLIPS)
   *     phase?: "incubation" | "irrigation",  // force a cycle commit (not counted as a flip)
   *     solved?: boolean,              // vine gone + REQUIRED_FLIPS + Irrigation + PUZZLE_SOLVED
   *   }
   *
   * `{ solved: true }` mirrors `onForceSolve` (vine gone, room left in
   * Irrigation) and additionally emits PUZZLE_SOLVED for downstream gating.
   */
  seed(state: Record<string, unknown>): void {
    if (state.vineShattered === true) this.vine.shatter({ instant: true });
    if (typeof state.flipsCompleted === "number") {
      this.flipsCompleted = Math.max(0, Math.min(REQUIRED_FLIPS, Math.floor(state.flipsCompleted)));
    }
    if (state.phase === "incubation" || state.phase === "irrigation") {
      this.conservatoryCycle.setPhaseImmediate(state.phase);
    }
    if (state.solved === true) {
      this.vine.shatter({ instant: true });
      this.flipsCompleted = REQUIRED_FLIPS;
      this.conservatoryCycle.setPhaseImmediate("irrigation");
      this.solvedEmitted = true;
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
    }
  }

  onForceSolve(): void {
    // The real solve path shatters the vine and runs two handle flips.
    // Force-solve skips that, but the room must still end in Irrigation
    // (the state the real solve leaves) and the vine must be gone so the
    // world agrees with the solved flag.
    this.vine?.shatter({ instant: true });
    this.flipsCompleted = REQUIRED_FLIPS;
    this.solvedEmitted = true;
    this.conservatoryCycle.setPhaseImmediate("irrigation");
  }

  dispose(): void {
    for (const crank of this.allCranks()) {
      crank.dispose();
    }
    this.vine.dispose();
  }

  /**
   * Dev/QA introspection hook used by the Babylon MCP bridge and the
   * playthrough recorder's `forceSpin` primitive. Each spinnable entry
   * exposes a no-arg `spin()` that advances its `ValveCrank` by exactly
   * one rhythm hit (`registerHit()`, the path a real TURN_TABLE_TOUCH_HIT
   * takes). Aliases: `valveA` → Incubation handle, `valveB` → Irrigation
   * handle, `dial` → `valveA` (single-target traces use `dial`).
   */
  getInternals(): {
    id: string;
    displayName: string;
    cranks: ValveCrank[];
    vine: PetrifiedVine;
    vineShattered: boolean;
    flipsCompleted: number;
    step: MasterValveStep;
    ctx: PuzzleContext;
    conservatoryCycle: ConservatoryCycle;
    dial: { spin: () => boolean; crank: ValveCrank };
    valveA: { spin: () => boolean; crank: ValveCrank };
    valveB: { spin: () => boolean; crank: ValveCrank };
  } {
    const wrap = (crank: ValveCrank) => ({ crank, spin: () => crank.registerHit() });
    const valveA = wrap(this.incubationHandle);
    const valveB = wrap(this.irrigationHandle);
    return {
      id: this.id,
      displayName: this.displayName,
      cranks: this.allCranks(),
      vine: this.vine,
      vineShattered: this.vine.isShattered,
      flipsCompleted: this.flipsCompleted,
      step: this.getStep(),
      ctx: this.ctx,
      conservatoryCycle: this.conservatoryCycle,
      dial: valveA,
      valveA,
      valveB,
    };
  }

  // ─────────────────────────────────────────────────────────────────

  private maybeSolve(): void {
    if (this.solvedEmitted || this.flipsCompleted < REQUIRED_FLIPS) return;
    this.solvedEmitted = true;
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
  }

  private toastChoked(): void {
    const now = performance.now();
    if (now - this.lastChokedToastAt < CHOKED_TOAST_COOLDOWN_MS) return;
    this.lastChokedToastAt = now;
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "A petrified vine chokes the regulator — shatter it together with Resonant Drop (F+M).",
    });
  }

  private nearestCrank(from: Vector3): ValveCrank | null {
    let nearest: ValveCrank | null = null;
    let nearestDist = CRANK_REACH;
    for (const crank of this.allCranks()) {
      const d = Vector3.Distance(from, crank.rootMesh.position);
      if (d < nearestDist) {
        nearest = crank;
        nearestDist = d;
      }
    }
    return nearest;
  }

  private allCranks(): ValveCrank[] {
    return [this.incubationHandle, this.irrigationHandle];
  }

  private findCrankByKey(key: string | undefined): ValveCrank | null {
    if (!key) return null;
    return this.allCranks().find((c) => c.key === key) ?? null;
  }
}

function above(p: Vector3, dy: number): { x: number; y: number; z: number } {
  return { x: p.x, y: p.y + dy, z: p.z };
}
